import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MAX_RANGE_DAYS, RANGE_PRESETS, rangeProblem, type TimeRange } from '../lib/opsQuery';

/** `2026-09-25T10:00` (entered as UTC) <-> ISO. */
const toLocalInput = (iso: string): string => (iso ? new Date(iso).toISOString().slice(0, 16) : '');
const hoursAgoIso = (h: number): string => new Date(Date.now() - h * 3_600_000).toISOString();
const fromLocalInput = (v: string): string => (v ? new Date(`${v}:00Z`).toISOString() : '');

const SEGMENT =
  'h-7 rounded-sm px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-xs';

/** Global time-range control (spec §99): presets relative to now, or an absolute UTC window. */
export function TimeRangePicker({ value, onChange }: { value: TimeRange; onChange: (r: TimeRange) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const [custom, setCustom] = useState(value.kind === 'custom');
  const [start, setStart] = useState(() => (value.kind === 'custom' ? toLocalInput(value.start) : toLocalInput(hoursAgoIso(1))));
  const [end, setEnd] = useState(() => (value.kind === 'custom' ? toLocalInput(value.end) : toLocalInput(hoursAgoIso(0))));
  const problem = custom && start && end ? rangeProblem(new Date(fromLocalInput(start)), new Date(fromLocalInput(end))) : null;
  const problemText = (() => {
    if (!problem) return null;
    const s = new Date(fromLocalInput(start));
    const e = new Date(fromLocalInput(end));
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return t('range.invalid');
    if (e <= s) return t('range.order');
    return t('range.tooLong', { days: MAX_RANGE_DAYS });
  })();

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('range.label')}>
      <div className="inline-flex flex-wrap items-center gap-0.5 rounded-md bg-muted p-0.5">
        {RANGE_PRESETS.map((p) => {
          const active = !custom && value.kind === 'preset' && value.preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              className={SEGMENT}
              aria-pressed={active}
              title={t('range.last', { value: t(`range.presets.${p.id}`, { defaultValue: p.label }) })}
              onClick={() => {
                setCustom(false);
                onChange({ kind: 'preset', preset: p.id });
              }}
            >
              {p.id}
            </button>
          );
        })}
        <button type="button" className={SEGMENT} aria-pressed={custom} onClick={() => setCustom(true)}>
          {t('range.custom')}
        </button>
      </div>
      {custom ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1">
            <Label htmlFor={`${id}-s`} className="text-xs text-muted-foreground">
              {t('range.from')}
            </Label>
            <Input id={`${id}-s`} type="datetime-local" className="h-8 w-auto" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor={`${id}-e`} className="text-xs text-muted-foreground">
              {t('range.to')}
            </Label>
            <Input id={`${id}-e`} type="datetime-local" className="h-8 w-auto" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!start || !end || problem !== null}
            onClick={() => onChange({ kind: 'custom', start: fromLocalInput(start), end: fromLocalInput(end) })}
          >
            {t('action.apply')}
          </Button>
          {problemText ? <span className="text-xs text-critical" role="alert">{problemText}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
